import { FieldError } from "../FieldError";
import { useIsMobile } from "../hooks";
import { Typo } from "../Typo";

type RadioGroupV2Props = {
  label?: string;
  value: string | null;
  options: { label: string; value: string; disabled?: boolean }[];
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
};

export const RadioGroupV2 = ({
  label,
  value,
  options,
  onChange,
  error,
  disabled,
}: RadioGroupV2Props) => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Typo
          element="h6"
          type={isMobile ? "labelSM" : "labelMD"}
          textColor="text-text-secondary"
        >
          {label}
        </Typo>
      )}

      <div className="flex flex-col gap-3">
        {options.map((option) => (
          <RadioV2
            key={option.value}
            label={option.label}
            value={option.value}
            checked={value === option.value}
            onChange={onChange}
            disabled={option.disabled || disabled}
          />
        ))}
      </div>
      {error && <FieldError text={error} />}
    </div>
  );
};

type RadioV2Props = {
  label: string;
  value: string;
  checked?: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
};

/**
 * Presentational radio circle (no `input`) — the visual only. Extracted so
 * non-`input` rows that need to look visually identical to a selected/
 * unselected radio (e.g. a navigation row that drills into a sub-screen) can
 * render a pixel-identical dot without duplicating the markup. Note this is
 * purely visual: it exposes no ARIA role or state, so it does not read as a
 * radio to assistive tech. `RadioV2` overlays a real `input` on top of this
 * for accessible selection.
 */
export const RadioDot = ({ checked }: { checked?: boolean }) => (
  <span
    className={`relative flex h-6 w-6 items-center justify-center rounded-full border-2 transition-all ${
      checked
        ? "border-surface-inverse-primary bg-surface-inverse-primary"
        : "border-stroke-secondary bg-white"
    }`}
  >
    {checked && <span className="h-3 w-3 rounded-full bg-white" />}
  </span>
);

const RadioV2 = ({
  label,
  value,
  checked,
  onChange,
  disabled,
}: RadioV2Props) => {
  return (
    <label
      htmlFor={`radio-${value}`}
      className={`flex cursor-pointer items-center gap-3 ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <span className="relative flex items-center justify-center">
        <input
          disabled={disabled}
          id={`radio-${value}`}
          type="radio"
          value={value}
          checked={checked}
          onChange={() => onChange(value)}
          className="peer sr-only"
        />
        <RadioDot checked={checked} />
      </span>
      <Typo element="span" type="bodySM">
        {label}
      </Typo>
    </label>
  );
};
